import React, { useEffect, useMemo, useRef, useState } from "react";
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Mesh group calls (best for small groups). Free STUN signaling over the app WebSocket.
export default function GroupCallOverlay({ chat, video, incoming, fromName, sendWs, onClose }) {
  const { user } = useAuth();
  const myId = user.id;
  const [joined, setJoined] = useState(!incoming);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(video);
  const [remotes, setRemotes] = useState({}); // peerId -> {name}
  const pcsRef = useRef({});
  const qualityRef = useRef({}); // peerId -> tier index (0 = best)
  const reconnectTimers = useRef({}); // peerId -> grace timeout
  const streamsRef = useRef({}); // peerId -> MediaStream
  const localRef = useRef(null);
  const localStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const joinedRef = useRef(joined);
  useEffect(() => { joinedRef.current = joined; }, [joined]);
  const iceRef = useRef([{ urls: "stun:stun.l.google.com:19302" }]);
  const chatId = chat?.id;

  const attachRemote = (peerId, stream, name) => {
    streamsRef.current[peerId] = stream;
    setRemotes((r) => ({ ...r, [peerId]: { name: name || "Member" } }));
    setTimeout(() => {
      const el = document.getElementById(`rc-remote-${peerId}`);
      if (el) {
        el.srcObject = stream;
        el.muted = false; // group audio flows through each peer's <video>
        const p = el.play(); if (p && p.catch) p.catch(() => {}); // iOS: explicit play once the track arrives
      }
    }, 50);
  };

  // iOS Safari: remote media only produces sound if a media element is played inside a
  // user gesture and the AudioContext is resumed. Prime this on the Join/Accept tap.
  const unlockAudio = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        if (!audioCtxRef.current) audioCtxRef.current = new AC();
        if (audioCtxRef.current.state === "suspended") audioCtxRef.current.resume().catch(() => {});
      }
    } catch (e) {}
    try {
      document.querySelectorAll('[id^="rc-remote-"]').forEach((el) => {
        el.muted = false; const p = el.play && el.play(); if (p && p.catch) p.catch(() => {});
      });
    } catch (e) {}
  };

  // Automatic per-peer quality ladder (mesh). Tier 0 = best.
  const TIERS = [
    { vBitrate: 2000000, vScale: 1,   vFps: 30, aBitrate: 40000 },
    { vBitrate: 1000000, vScale: 1.5, vFps: 30, aBitrate: 32000 },
    { vBitrate: 450000,  vScale: 2,   vFps: 20, aBitrate: 24000 },
    { vBitrate: 160000,  vScale: 3,   vFps: 15, aBitrate: 16000 },
  ];
  const applyQualityTo = async (pc, idx) => {
    const tier = TIERS[Math.max(0, Math.min(TIERS.length - 1, idx))];
    for (const sender of pc.getSenders()) {
      if (!sender.track) continue;
      const params = sender.getParameters();
      if (!params.encodings || !params.encodings.length) params.encodings = [{}];
      if (sender.track.kind === "video") {
        params.degradationPreference = "balanced";
        params.encodings[0].maxBitrate = tier.vBitrate;
        params.encodings[0].scaleResolutionDownBy = tier.vScale;
        params.encodings[0].maxFramerate = tier.vFps;
      } else {
        params.encodings[0].maxBitrate = tier.aBitrate;
      }
      try { await sender.setParameters(params); } catch (e) {}
    }
  };

  const dropPeer = (peerId) => {
    const pc = pcsRef.current[peerId];
    if (pc) { try { pc.close(); } catch (e) {} }
    delete pcsRef.current[peerId];
    delete streamsRef.current[peerId];
    clearTimeout(reconnectTimers.current[peerId]);
    delete reconnectTimers.current[peerId];
    setRemotes((r) => { const n = { ...r }; delete n[peerId]; return n; });
  };

  // On a dropped peer link, the offerer re-negotiates with an ICE restart; if it can't
  // recover within the grace window we remove just that participant.
  const schedulePeerReconnect = async (peerId) => {
    const pc = pcsRef.current[peerId];
    if (!pc) return;
    if (!reconnectTimers.current[peerId]) {
      reconnectTimers.current[peerId] = setTimeout(() => {
        const p = pcsRef.current[peerId];
        if (p && p.connectionState !== "connected") dropPeer(peerId);
      }, 20000);
    }
    if (myId < peerId) {
      try {
        if (pc.restartIce) pc.restartIce();
        const offer = await pc.createOffer({ iceRestart: true });
        await pc.setLocalDescription(offer);
        sendWs({ type: "rtc_offer", to: peerId, group: true, chat_id: chatId, sdp: offer, video, reconnect: true });
      } catch (e) {}
    }
  };

  const makePc = (peerId, name) => {
    if (pcsRef.current[peerId]) return pcsRef.current[peerId];
    const pc = new RTCPeerConnection({ iceServers: iceRef.current });
    pcsRef.current[peerId] = pc;
    pc.onicecandidate = (e) => { if (e.candidate) sendWs({ type: "rtc_ice", to: peerId, group: true, chat_id: chatId, candidate: e.candidate }); };
    pc.ontrack = (e) => attachRemote(peerId, e.streams[0], name);
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (["disconnected", "failed"].includes(st)) schedulePeerReconnect(peerId);
      if (st === "connected") { clearTimeout(reconnectTimers.current[peerId]); delete reconnectTimers.current[peerId]; }
    };
    const ls = localStreamRef.current;
    if (ls) ls.getTracks().forEach((t) => pc.addTrack(t, ls));
    qualityRef.current[peerId] = qualityRef.current[peerId] ?? 0;
    applyQualityTo(pc, qualityRef.current[peerId]);
    return pc;
  };

  const offerTo = async (peerId, name) => {
    const pc = makePc(peerId, name);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    sendWs({ type: "rtc_offer", to: peerId, group: true, chat_id: chatId, sdp: offer, video });
  };

  const join = async () => {
    try {
      unlockAudio();
      const { data } = await api.get("/rtc/ice");
      iceRef.current = data.iceServers;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
        video: video ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 } } : false,
      });
      localStreamRef.current = stream;
      const vt0 = stream.getVideoTracks()[0];
      if (vt0) { try { vt0.contentHint = "motion"; } catch (e) {} }
      if (localRef.current) localRef.current.srcObject = stream;
      setJoined(true);
      sendWs({ type: "rtc_group_join", chat_id: chatId });
    } catch (e) { toast.error("Camera/microphone unavailable"); onClose(); }
  };

  const leave = () => {
    sendWs({ type: "rtc_group_leave", chat_id: chatId });
    Object.values(pcsRef.current).forEach((pc) => { try { pc.close(); } catch (e) {} });
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    try { audioCtxRef.current?.close(); audioCtxRef.current = null; } catch (e) {}
    onClose();
  };

  useEffect(() => {
    // outgoing: announce the call, then join
    if (!incoming) {
      sendWs({ type: "rtc_group_call", chat_id: chatId, video });
      join();
    }
    const onRtc = async (ev) => {
      const m = ev.detail;
      if (m.chat_id && m.chat_id !== chatId) return;
      if (!joinedRef.current) return; // ignore until we join
      const peerId = m.from;
      if (!peerId || peerId === myId) return;
      if (m.type === "rtc_group_join") {
        sendWs({ type: "rtc_group_ack", to: peerId, chat_id: chatId });
        if (myId < peerId) offerTo(peerId, m.from_name);
      } else if (m.type === "rtc_group_ack") {
        if (myId < peerId) offerTo(peerId, m.from_name);
      } else if (m.type === "rtc_offer" && m.group) {
        const pc = makePc(peerId, m.from_name);
        await pc.setRemoteDescription(new RTCSessionDescription(m.sdp));
        const ans = await pc.createAnswer();
        await pc.setLocalDescription(ans);
        sendWs({ type: "rtc_answer", to: peerId, group: true, chat_id: chatId, sdp: ans });
      } else if (m.type === "rtc_answer" && m.group) {
        const pc = pcsRef.current[peerId];
        if (pc) await pc.setRemoteDescription(new RTCSessionDescription(m.sdp));
      } else if (m.type === "rtc_ice" && m.group) {
        const pc = pcsRef.current[peerId];
        if (pc) { try { await pc.addIceCandidate(new RTCIceCandidate(m.candidate)); } catch (e) {} }
      } else if (m.type === "rtc_group_leave") {
        const pc = pcsRef.current[peerId];
        if (pc) pc.close();
        delete pcsRef.current[peerId];
        delete streamsRef.current[peerId];
        setRemotes((r) => { const n = { ...r }; delete n[peerId]; return n; });
      }
    };
    window.addEventListener("rc-rtc", onRtc);
    return () => {
      window.removeEventListener("rc-rtc", onRtc);
      Object.values(reconnectTimers.current).forEach((tm) => clearTimeout(tm));
      Object.values(pcsRef.current).forEach((pc) => { try { pc.close(); } catch (e) {} });
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep a ref of joined for the event handler closure
  const toggleMic = () => { const t = localStreamRef.current?.getAudioTracks()[0]; if (t) { t.enabled = !t.enabled; setMicOn(t.enabled); } };
  const toggleCam = () => { const t = localStreamRef.current?.getVideoTracks()[0]; if (t) { t.enabled = !t.enabled; setCamOn(t.enabled); } };

  // Automatically adapt each peer link to its own network conditions.
  useEffect(() => {
    const counters = {}; // peerId -> {poor, good}
    const id = setInterval(async () => {
      for (const [peerId, pc] of Object.entries(pcsRef.current)) {
        if (!pc || pc.connectionState !== "connected") continue;
        try {
          const stats = await pc.getStats();
          let rtt = 0, loss = 0, availOut = 0, bytesIn = 0;
          stats.forEach((r) => {
            if (r.type === "candidate-pair" && r.nominated) {
              if (r.currentRoundTripTime != null) rtt = r.currentRoundTripTime;
              if (r.availableOutgoingBitrate != null) availOut = r.availableOutgoingBitrate;
            }
            if (r.type === "remote-inbound-rtp" && r.fractionLost != null) loss = Math.max(loss, r.fractionLost);
            if (r.type === "inbound-rtp" && r.bytesReceived != null) bytesIn += r.bytesReceived;
          });
          const idx = qualityRef.current[peerId] ?? 0;
          const cur = TIERS[idx];
          const upTier = TIERS[Math.max(0, idx - 1)];
          const c = counters[peerId] || (counters[peerId] = { poor: 0, good: 0, bytes: 0, stall: 0 });
          // Proactive media-stall detection → early ICE restart for this peer.
          if (bytesIn > 0 && bytesIn === c.bytes) c.stall++; else c.stall = 0;
          c.bytes = bytesIn;
          if (c.stall >= 2) { schedulePeerReconnect(peerId); c.stall = 0; }
          const poorSample = loss > 0.08 || rtt > 0.5 || (availOut > 0 && availOut < cur.vBitrate * 0.8);
          const goodSample = loss < 0.03 && rtt < 0.3 && (availOut === 0 || availOut > upTier.vBitrate * 1.3);
          if (poorSample) { c.poor++; c.good = 0; } else if (goodSample) { c.good++; c.poor = 0; } else { c.poor = 0; c.good = 0; }
          let want = idx;
          if (c.poor >= 2 && idx < TIERS.length - 1) want = idx + 1;
          else if (c.good >= 3 && idx > 0) want = idx - 1;
          if (want !== idx) { qualityRef.current[peerId] = want; applyQualityTo(pc, want); c.poor = 0; c.good = 0; }
        } catch (e) {}
      }
    }, 2500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React instantly to the device losing/regaining its network.
  useEffect(() => {
    const onOffline = () => { Object.keys(pcsRef.current).forEach((pid) => schedulePeerReconnect(pid)); };
    const onOnline = () => { Object.keys(pcsRef.current).forEach((pid) => { if (myId < pid) schedulePeerReconnect(pid); }); };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => { window.removeEventListener("offline", onOffline); window.removeEventListener("online", onOnline); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const remoteIds = Object.keys(remotes);
  const cols = remoteIds.length <= 1 ? "grid-cols-1" : remoteIds.length <= 3 ? "grid-cols-2" : "grid-cols-3";

  if (incoming && !joined) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center" data-testid="group-call-incoming">
        <div className="h-24 w-24 rounded-full bg-[var(--rc-gold)]/15 grid place-items-center text-[var(--rc-gold)] mb-4 rc-ring"><Users size={40} /></div>
        <h2 className="font-heading text-3xl text-white mb-1">{chat?.title || "Group"}</h2>
        <p className="text-[var(--rc-gold)] mb-10">Incoming group {video ? "video" : "audio"} call · {fromName}</p>
        <div className="flex gap-6">
          <button onClick={leave} data-testid="group-reject-btn" className="h-16 w-16 grid place-items-center rounded-full bg-red-500 text-white"><PhoneOff size={26} /></button>
          <button onClick={join} data-testid="group-answer-btn" className="h-16 w-16 grid place-items-center rounded-full bg-emerald-500 text-white rc-ring"><Phone size={26} /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col p-4" data-testid="group-call-overlay">
      <div className="text-center mb-3">
        <h2 className="font-heading text-2xl text-white">{chat?.title || "Group call"}</h2>
        <p className="text-[var(--rc-gold)] text-sm">{remoteIds.length + 1} on the call</p>
      </div>
      <div className={`flex-1 grid ${cols} gap-3 place-items-center overflow-auto`}>
        <div className="relative w-full h-full min-h-[160px] rounded-2xl overflow-hidden border border-[var(--rc-gold)]/40 bg-black">
          <video ref={localRef} autoPlay playsInline muted className="w-full h-full object-cover" />
          <span className="absolute bottom-2 left-2 text-xs text-white bg-black/50 px-2 py-0.5 rounded-full">You</span>
        </div>
        {remoteIds.map((pid) => (
          <div key={pid} className="relative w-full h-full min-h-[160px] rounded-2xl overflow-hidden border border-white/10 bg-black">
            <video id={`rc-remote-${pid}`} autoPlay playsInline className="w-full h-full object-cover" />
            <span className="absolute bottom-2 left-2 text-xs text-white bg-black/50 px-2 py-0.5 rounded-full">{remotes[pid].name}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-4 mt-4">
        <button onClick={toggleMic} className="h-14 w-14 grid place-items-center rounded-full bg-white/10 text-white" data-testid="group-mic-btn">{micOn ? <Mic size={22} /> : <MicOff size={22} />}</button>
        {video && <button onClick={toggleCam} className="h-14 w-14 grid place-items-center rounded-full bg-white/10 text-white" data-testid="group-cam-btn">{camOn ? <Video size={22} /> : <VideoOff size={22} />}</button>}
        <button onClick={leave} className="h-16 w-16 grid place-items-center rounded-full bg-red-500 text-white" data-testid="group-hangup-btn"><PhoneOff size={26} /></button>
      </div>
    </div>
  );
}
